// NEW PARTY v2 — batch 3's rebuild of NewParty (UI rebuild; drawn when lib/ui-v2.ts switches batch 3 on).
//
// Astra's anatomy (02-screen-review/new-party), the operator's light ruling of 26 Oct: one card — the Role first
// (Customer, Merchant, Own account), then only the fields that kind needs; the optional ones (Telegram, tier, profile,
// merchant role, capabilities, an account's label and parent group, notes) folded under "More"; "Review party" turns
// into the readback (with a same-name check against Parties, information only) and the "Create party" confirm.
//
// Own account stays here: Treasury has no create door yet (verification 4.4). THE STATE MACHINE IS TODAY'S, COPIED
// (screens/NewParty.tsx): every field and default, the kind's usual currency, the slug built from the name with its
// collision tail, the groups read with readState, the createParty bodies, canSubmit, the success navigation.
import { useState, useMemo, useEffect } from 'react';
import { readWords } from '../../lib/errors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { tg } from '../../lib/telegram';
import { MoreDetails } from '../../components/MoreDetails';
import { Combobox, type ComboboxOption } from '../../components/Combobox';
import { Chip } from '../../components/FilterRail';
import { Field, FieldRow } from '../../components/Field';
import { ErrorLine } from '../../components/ErrorLine';
import { ServerLine, StateLine } from '../../components/v2/kit';
import { partyName, partyTypeLabel, trustTierLabel } from '../../lib/naming';
import { usePartySearch } from '../../lib/roster-cut';
import { ASSETS, isFiat } from '../../lib/workflow';
import { displayAsset } from '../../lib/format';
import { readState } from '../../lib/read-state';
import { FormFoot, FormFrame, PRIMARY, QUIET } from '../../components/v2/move/FormFrame';

type PartyType = 'customer' | 'counterparty' | 'own_account';
type ProfileKind = 'tracked' | 'treasury_only';
type CounterpartyRole = 'merchant' | 'other';
type TrustTier = 'new' | 'standard' | 'repeat' | 'vip';
type RailKind = 'bank' | 'exchange' | 'hot' | 'cold' | 'cash' | 'angadiya';

const COUNTERPARTY_ROLE_LABELS: Record<CounterpartyRole, string> = { merchant: 'Merchant', other: 'Other' };
const RAIL_KIND_LABELS: Record<RailKind, string> = {
  bank: 'Bank', exchange: 'Exchange', hot: 'Hot wallet', cold: 'Cold wallet', cash: 'Cash', angadiya: 'Angadiya',
};
const CAPABILITY_LABELS: Record<string, string> = {
  'mc': "Manager's cheque", 'passthrough': 'Passthrough', 'bank-transfer': 'Bank transfer', 'cash': 'Cash', 'crypto': 'Crypto',
};
const CAPABILITIES = ['mc', 'passthrough', 'bank-transfer', 'cash', 'crypto'] as const;

// WHAT A CASH OR BANK ACCOUNT MAY HOLD (14 Sept 2026): the books refuse to open an asset on a rail from a posting.
const FIAT_RAIL_KINDS: ReadonlySet<RailKind> = new Set(['bank', 'cash', 'angadiya']);
const FIATS: readonly string[] = ASSETS.filter((a) => isFiat(a));
const usualFiatFor = (kind: RailKind): string => (kind === 'angadiya' ? 'INR' : 'AED');

function safeSlug(s: string): string {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30);
}
function withCollisionSuffix(base: string): string {
  const suffix = Math.random().toString(36).slice(2, 6);
  return `${base}-${suffix}`;
}

const PROVIDER_HINT: Record<RailKind, string> = {
  bank: 'ENBD / Wio / ADCB', exchange: 'Binance / Bitget / OKX', hot: 'TRC / ERC / BEP',
  cold: 'Ledger / Trezor', cash: 'DXB / Burjuman / Office', angadiya: 'PR / merchant name',
};
const LABEL_HINT: Record<RailKind, string> = {
  bank: 'NK / Loki / Main', exchange: 'Anil / Esub / DXB', hot: 'V8 / 06',
  cold: 'My / Loki', cash: 'EUR / USD', angadiya: 'Main / V2',
};

export function NewPartyV2() {
  const nav = useNavigate();
  const qc  = useQueryClient();

  // ── Today's state, verbatim (NewParty.tsx) ─────────────────────────────────────────────────────────────────────
  const [partyType, setPartyType] = useState<PartyType>('customer');
  const [notes, setNotes]         = useState('');
  const [custDisplayName, setCustDisplayName]   = useState('');
  const [profileKind, setProfileKind]           = useState<ProfileKind>('tracked');
  const [telegramUsername, setTelegramUsername] = useState('');
  const [trustTier, setTrustTier]               = useState<TrustTier>('new');
  const [cpDisplayName, setCpDisplayName] = useState('');
  const [counterpartyRole, setCounterpartyRole] = useState<CounterpartyRole>('merchant');
  const [capabilities, setCapabilities]   = useState<string[]>([]);
  const [ownSubtype, setOwnSubtype]   = useState<'rail' | 'group'>('rail');
  const [ownKind, setOwnKind]         = useState<RailKind>('bank');
  const [ownProvider, setOwnProvider] = useState('');
  const [ownLabel, setOwnLabel]       = useState('');
  const [groupName, setGroupName]     = useState('');
  const [parentSlug, setParentSlug]   = useState('');
  const [ownAssets, setOwnAssets]     = useState<string[]>([usualFiatFor('bank')]);
  const pickKind = (k: RailKind) => { setOwnKind(k); setOwnAssets([usualFiatFor(k)]); };
  const fiatRail = ownSubtype === 'rail' && FIAT_RAIL_KINDS.has(ownKind);

  const ownSlugAuto = useMemo(() => {
    const p = safeSlug(ownProvider);
    const l = safeSlug(ownLabel);
    if (!p) return '';
    return [ownKind, p, l].filter(Boolean).join('.');
  }, [ownKind, ownProvider, ownLabel]);
  const ownDisplayAuto = useMemo(() => {
    const parts = [ownProvider.trim(), ownLabel.trim()].filter(Boolean);
    return parts.length ? parts.join(' · ') : '';
  }, [ownProvider, ownLabel]);

  const existingGroups = useQuery({
    queryKey: ['parties', 'groups'],
    queryFn:  () => api.listParties({ entity: 'dubaiotc', is_group: true }),
    enabled:  partyType === 'own_account' && ownSubtype === 'rail',
  });
  const groups = readState(existingGroups);
  const parentOptions: ComboboxOption[] = useMemo(() =>
    (existingGroups.data?.parties ?? []).slice(0, 30).map((p) => ({
      value: p.slug, label: partyName(p),
    }))
  , [existingGroups.data]);

  const submit = useMutation({
    mutationFn: () => {
      const common = { entity: 'dubaiotc' as const, notes: notes || null };
      if (partyType === 'customer') {
        return api.createParty({
          ...common,
          party_type:        'customer',
          slug:              withCollisionSuffix(safeSlug(custDisplayName)),
          display_name:      custDisplayName.trim(),
          profile_kind:      profileKind,
          telegram_username: telegramUsername || null,
          trust_tier:        trustTier,
        });
      }
      if (partyType === 'counterparty') {
        return api.createParty({
          ...common,
          party_type:                'counterparty',
          slug:                      withCollisionSuffix(safeSlug(cpDisplayName)),
          display_name:              cpDisplayName.trim(),
          counterparty_role:         counterpartyRole,
          counterparty_capabilities: capabilities,
        });
      }
      if (ownSubtype === 'group') {
        return api.createParty({
          ...common,
          party_type:    'own_account',
          slug:          withCollisionSuffix(safeSlug(groupName)),
          display_name:  groupName.trim(),
          treasury_slug: null,
        });
      }
      return api.createParty({
        ...common,
        party_type:        'own_account',
        slug:              ownSlugAuto,
        display_name:      (ownDisplayAuto || ownSlugAuto).trim(),
        treasury_slug:     ownSlugAuto || null,
        parent_party_slug: parentSlug || null,
        assets:            fiatRail ? ownAssets : undefined,
      });
    },
    onSuccess: (data) => {
      tg()?.HapticFeedback.notificationOccurred('success');
      qc.invalidateQueries({ queryKey: ['parties'] });
      nav(`/parties/${encodeURIComponent(data.party.slug)}?entity=dubaiotc`);
    },
    onError: () => tg()?.HapticFeedback.notificationOccurred('error'),
  });

  const validSlugRe = /^[a-z0-9][a-z0-9._-]{1,63}$/;
  const canSubmit = !submit.isPending && (() => {
    if (partyType === 'customer')     return !!custDisplayName.trim() && safeSlug(custDisplayName).length > 0;
    if (partyType === 'counterparty') return !!cpDisplayName.trim()   && safeSlug(cpDisplayName).length   > 0;
    if (ownSubtype === 'group')       return !!groupName.trim()       && safeSlug(groupName).length       > 0;
    if (fiatRail && ownAssets.length === 0) return false;
    return !!ownProvider.trim() && validSlugRe.test(ownSlugAuto);
  })();

  // ── The review step (Astra's "Review party"). Any edit goes back to the form. ─────────────────────────────────
  const [reviewing, setReviewing] = useState(false);
  useEffect(() => { setReviewing(false); }, [partyType, custDisplayName, profileKind, telegramUsername, trustTier,
    cpDisplayName, counterpartyRole, capabilities, ownSubtype, ownKind, ownProvider, ownLabel, groupName, parentSlug, ownAssets, notes]);
  // The confirm stays on screen while its post is in flight (canSubmit is false while pending).
  const confirming = reviewing && (canSubmit || submit.isPending);

  // The name the party will carry, and a same-name check against Parties — information, never a block. Said only from
  // a read that succeeded; a failed read says it could not check.
  const willBe = partyType === 'customer' ? custDisplayName.trim()
    : partyType === 'counterparty' ? cpDisplayName.trim()
      : ownSubtype === 'group' ? groupName.trim() : (ownDisplayAuto || ownSlugAuto).trim();
  const twins = usePartySearch({
    base: ['parties', 'dubaiotc'], text: willBe,
    ask: (q) => api.listParties({ entity: 'dubaiotc', q }),
    enabled: confirming && willBe.length >= 2,
  });
  const twinRead = readState(twins);
  const twin = twinRead === 'known'
    ? (twins.data?.parties ?? []).find((p) => partyName(p).trim().toLowerCase() === willBe.toLowerCase())
    : undefined;

  const segmented = (active: boolean) => `seg whitespace-nowrap ${active ? 'seg-on' : ''}`;
  const kindWord = partyType === 'own_account' ? (ownSubtype === 'group' ? 'Group' : 'Own account') : partyTypeLabel(partyType);

  return (
    <FormFrame title="New party">
      <FieldRow>
        <Field label="Role">
          <select className="select" value={partyType} onChange={(e) => setPartyType(e.target.value as PartyType)}>
            {(['customer', 'counterparty', 'own_account'] as const).map((v) => (
              <option key={v} value={v}>{partyTypeLabel(v)}</option>
            ))}
          </select>
        </Field>
        {partyType === 'customer' && (
          <Field label="Name">
            <input className="input" value={custDisplayName} onChange={(e) => setCustDisplayName(e.target.value)} />
          </Field>
        )}
        {partyType === 'counterparty' && (
          <Field label="Name">
            <input className="input" value={cpDisplayName} onChange={(e) => setCpDisplayName(e.target.value)} />
          </Field>
        )}
        {partyType === 'own_account' && (
          <Field label="Type">
            <div className="flex gap-2">
              {([
                { v: 'rail',  l: 'Account', d: 'One account (a bank account, hot wallet, cash drawer)' },
                { v: 'group', l: 'Group',   d: 'A parent (Anil / Bhargav / Sai) — groups accounts; holds no balance' },
              ] as const).map((t) => (
                <button key={t.v} type="button" onClick={() => setOwnSubtype(t.v)} className={segmented(ownSubtype === t.v)} title={t.d}>
                  {t.l}
                </button>
              ))}
            </div>
          </Field>
        )}
      </FieldRow>

      {partyType === 'own_account' && ownSubtype === 'group' && (
        <Field label="Group name">
          <input className="input" value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Anil / Bhargav / Sai" />
        </Field>
      )}

      {partyType === 'own_account' && ownSubtype === 'rail' && (
        <>
          <FieldRow>
            <Field label="Kind">
              <select className="select" value={ownKind} onChange={(e) => pickKind(e.target.value as RailKind)}>
                {(Object.keys(RAIL_KIND_LABELS) as RailKind[]).map((k) => <option key={k} value={k}>{RAIL_KIND_LABELS[k]}</option>)}
              </select>
            </Field>
            <Field label="Provider">
              <input className="input" value={ownProvider} onChange={(e) => setOwnProvider(e.target.value)} placeholder={PROVIDER_HINT[ownKind]} />
            </Field>
          </FieldRow>
          {/* The currencies a cash / bank / angadiya account may hold — at least one, or it takes no postings. */}
          {fiatRail && (
            <Field label="May hold">
              <div className="flex flex-wrap items-center gap-1.5">
                {FIATS.map((code) => {
                  const on = ownAssets.includes(code);
                  return (
                    <Chip key={code} active={on} onClick={() => setOwnAssets(on ? ownAssets.filter((x) => x !== code) : [...ownAssets, code])}>
                      {displayAsset(code)}
                    </Chip>
                  );
                })}
              </div>
              {ownAssets.length === 0 && <ErrorLine>Pick at least one — it takes no postings otherwise.</ErrorLine>}
            </Field>
          )}
        </>
      )}

      {/* MORE — what each kind may carry but does not need. */}
      <div className="border-t border-stroke-soft pt-3">
        <MoreDetails label="More">
          {partyType === 'customer' && (
            <>
              <Field label="Profile">
                <div className="flex gap-2">
                  {([
                    { v: 'tracked',       l: 'Tracked',       d: 'KYC required'    },
                    { v: 'treasury_only', l: 'Treasury-only', d: 'No KYC; name only' },
                  ] as const).map((k) => (
                    <button key={k.v} type="button" onClick={() => setProfileKind(k.v)} className={segmented(profileKind === k.v)} title={k.d}>
                      {k.l}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Telegram username">
                <input className="input" value={telegramUsername} onChange={(e) => setTelegramUsername(e.target.value)} placeholder="@username" />
              </Field>
              <Field label="Trust tier">
                <select className="select" value={trustTier} onChange={(e) => setTrustTier(e.target.value as TrustTier)}>
                  {(['new', 'standard', 'repeat', 'vip'] as const).map((t) => <option key={t} value={t}>{trustTierLabel(t)}</option>)}
                </select>
              </Field>
            </>
          )}
          {partyType === 'counterparty' && (
            <>
              <Field label="Merchant role">
                <select className="select" value={counterpartyRole} onChange={(e) => setCounterpartyRole(e.target.value as CounterpartyRole)}>
                  {(Object.entries(COUNTERPARTY_ROLE_LABELS) as Array<[CounterpartyRole, string]>).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Field>
              <Field label="Capabilities">
                <div className="flex flex-wrap gap-1.5">
                  {CAPABILITIES.map((cap) => {
                    const on = capabilities.includes(cap);
                    return (
                      <button key={cap} type="button" onClick={() => setCapabilities(on ? capabilities.filter((c) => c !== cap) : [...capabilities, cap])}
                        className={`chip ${on ? 'chip-on' : ''}`}>
                        {CAPABILITY_LABELS[cap] ?? cap}
                      </button>
                    );
                  })}
                </div>
              </Field>
            </>
          )}
          {partyType === 'own_account' && ownSubtype === 'rail' && (
            <>
              <Field label="Label">
                <input className="input" value={ownLabel} onChange={(e) => setOwnLabel(e.target.value)} placeholder={LABEL_HINT[ownKind]} />
              </Field>
              <Field label="Parent group">
                <Combobox
                  value={parentSlug}
                  onChange={setParentSlug}
                  options={parentOptions}
                  placeholder="Pick group"
                  emptyHint={groups === 'failed' ? 'Groups could not be read.' : 'No such group.'}
                  loading={existingGroups.isFetching || groups === 'waiting'}
                />
                {groups === 'failed' ? (
                  <StateLine tone="warn" className="mt-1" more={readWords(existingGroups.error)}
                    action={<button type="button" className="btn-link text-meta" onClick={() => existingGroups.refetch()}>Retry</button>}>
                    Couldn&rsquo;t read groups — try again.
                  </StateLine>
                ) : groups === 'waiting' ? (
                  <p className="mt-1 text-meta text-tg-hint">Reading groups…</p>
                ) : parentOptions.length === 0 && (
                  <p className="mt-1 text-meta text-tg-hint">
                    No groups yet ·{' '}
                    <button type="button" className="btn-link text-meta" onClick={() => setOwnSubtype('group')}>Create a group</button>
                  </p>
                )}
              </Field>
            </>
          )}
          <Field label="Notes">
            <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </MoreDetails>
      </div>

      {/* THE READBACK — what will be created, in the name it will carry; a same-name party is said, never blocked. */}
      {confirming && (
        <div className="space-y-1">
          <p className="text-body text-tg-text">{kindWord} · <span className="who">{willBe}</span></p>
          {twin ? (
            <StateLine tone="warn" action={<Link to={`/parties/${encodeURIComponent(twin.slug)}`} className="btn-link text-meta">Open →</Link>}>
              Already in Parties: {partyName(twin)}
            </StateLine>
          ) : twinRead === 'failed' && (
            <p className="text-meta text-tg-hint">Couldn&rsquo;t check for the same name.</p>
          )}
        </div>
      )}

      {/* The refusal above the button, read before the next tap. words:ok — the books' own sentence. */}
      {submit.isError && <ServerLine error={submit.error} />}

      <FormFoot>
        {confirming ? (
          <>
            <button type="button" className={QUIET} disabled={submit.isPending} onClick={() => setReviewing(false)}>Edit</button>
            <button className={PRIMARY} disabled={!canSubmit} onClick={() => submit.mutate()}>
              {submit.isPending ? 'Creating…' : 'Create party'}
            </button>
          </>
        ) : (
          <button className={PRIMARY} disabled={!canSubmit} onClick={() => setReviewing(true)}>Review party →</button>
        )}
      </FormFoot>
    </FormFrame>
  );
}
